/* eslint-disable jsx-a11y/label-has-for */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useCallback, useEffect, useState } from 'react';
import Select from 'react-select';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import IconButton from 'components/IconButton';
// import ContactForm from 'components/forms/contacts/ContactForm';
import { Button, Row, Col } from 'react-bootstrap';
import FormInput from 'components/forms/FormInput';
import { useIntl } from 'react-intl';
import { addNotification } from 'actions/NotificationsActions';
// import { getSalutationOptions } from 'helpers/clients';
import FormAutocomplete from 'components/forms/FormAutocomplete';
import { COUNTRY_DEFAULT } from 'helpers/clients';
import { entityMetaMapping, MetaForm, intlModule } from '@chedri/base';
import _isEmpty from 'lodash/isEmpty';
import Collapsible from 'components/layout/Collapsible';
import CartWizardContactForm from 'components/layout/cart/CartWizardContactForm';

const { address: addressMetaId } = entityMetaMapping;
const DELIVERY = 'delivery';
const INVOICE = 'invoice';

/**
 * Component to select Contact and Address
 * @param {Object} params
 * @param {string} params.username
 * @param {function} params.onAddressUpdate
 * @param {Array} params.defaultAddresses
 * @param {string} params.addressType one of "shippingAddress" or "invoiceAddress"
 *
 * @returns {JSX.Element}
 */
function QuoteAddress({ username, onAddressUpdate, defaultAddresses, addressType, contactReferenceId }) {
  const { messages } = useIntl();
  const dispatch = useDispatch();

  const addressTypeMap = {
    shippingAddress: DELIVERY,
    invoiceAddress: INVOICE,
  };
  if (!Object.hasOwnProperty.call(addressTypeMap, addressType)) {
    console.error(`Invalid addressType: ${addressType}`);
  }
  const addressTypeShort = addressTypeMap[addressType]; // INVOICE or DELIVERY

  const iconMap = {
    [DELIVERY]: 'fa-truck',
    [INVOICE]: 'fa-file-invoice',
  };
  const icon = iconMap[addressTypeShort];

  const labelMap = {
    [DELIVERY]: messages.address_delivery,
    [INVOICE]: messages.address_invoice,
  };
  const label = labelMap[addressTypeShort];

  const countriesPending = useSelector(intlModule.countries.selectors.isListLoading);
  const countries = useSelector(intlModule.countries.selectors.getList);
  useEffect(() => {
    if (!countries.length && !countriesPending) {
      dispatch(intlModule.countries.fetchList());
    }
  }, [countries, countriesPending]);

  // two views, mutually exclusive:
  const [isSelectVisible, setIsSelectVisible] = useState(true);
  const [isNewAddressFormVisible, setIsNewAddressFormVisible] = useState(false);

  // view switching
  const setVisibility = ({ select = false, address = false }) => {
    setIsSelectVisible(select);
    setIsNewAddressFormVisible(address);
  };
  const showSelectForm = () => setVisibility({ select: true });
  const showNewAddressForm = () => setVisibility({ address: true });

  const addressDefaults = {
    types: [addressTypeShort], // we'll create a new delivery or invoice address
    owner: null,
    target: { username },
  };
  const [newAddressData, setNewAddressData] = useState({ ...addressDefaults });

  const [pending, setPending] = useState(false);
  const [submitted, setSubmitted] = useState(false); // isCanValidate

  // Collection of Client Contacts
  const [contacts, setContacts] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null); // selected Contact ObjectId
  const getSelectedContact = () => (contacts || []).filter(({ id }) => id === selectedContactId).pop();

  // Collection of selected Addresses of the selected Contact
  const [contactAddresses, setContactAddresses] = useState([]);
  const [billingContactId, setBillingContactId] = useState(contactReferenceId);
  const [selectedContactAddressId, setSelectedContactAddressId] = useState(null); // selected Address ObjectId
  const getSelectedAddress = () => (contactAddresses || []).filter(({ id }) => id === selectedContactAddressId).pop();

  // Extract values for the Select component
  const contactToValue = contact => contact.id;
  const contactToLabel = ({ company, first_name: firstName, last_name: lastName }) =>
    [firstName, lastName, company].join(' ');

  const addressToValue = ({ id }) => id;
  const addressToLabel = ({ city, street, house_number: houseNumber }) => [city, street, houseNumber].join(' ');

  useEffect(() => {
    const contact = getSelectedContact();
    const address = getSelectedAddress();
    if (contact && address) {
      onAddressUpdate(addressType, contact, address);
    }
  }, [selectedContactId, selectedContactAddressId]);

  // fetch Contacts for the selected username
  useEffect(() => {
    const url = `/api/clients/${username}/defaultbillingcontacts.json`;
    axios
      .get(url)
      .then(response => {
        const defaultBillingContact = response.data;
        getContactsList(defaultBillingContact, username);
      })
      .catch(error => {
        console.error(error);
        dispatch(addNotification(500, error));
        getContactsList(null, username);
      });
  }, [username]);

  function getContactsList(defaultBillingContact, username) {
      const url = `/api/clients/${username}/contacts.json`;
      axios
        .get(url)
        .then(response => {
          const { list } = response.data;

          if (defaultBillingContact) {
            setBillingContactId(defaultBillingContact.id);
          }

          if (
            defaultBillingContact &&
            !list.some(
              contact => contact.id === defaultBillingContact.id
            )
          ) {
            list.unshift(defaultBillingContact);
          }
          setContacts(list);
        })
        .catch(error => {
          console.error(error);
          dispatch(addNotification(500, error));
        });
    }

  // Auto-select first contact from newly set Contacts
  useEffect(() => {
    if ((selectedContactId === undefined || selectedContactId === null) && contacts.length) {
      const firstContact = contacts[0];
      const { id } = firstContact;
      if (defaultAddresses?.contact?.id !== undefined) {
        setSelectedContactId(defaultAddresses.contact.id);
      } else if (billingContactId) {
        setSelectedContactId(billingContactId);
      } else {
        setSelectedContactId(id);
      }
    }
  }, [contacts]);

  // addresses for the newly selected Contact
  useEffect(() => {
    if (selectedContactId === undefined || selectedContactId === null) return;
    const contact = getSelectedContact();
    if (!contact) return;
    const { addresses } = contact;
    setContactAddresses(addresses);
    const firstAddress = addresses.length ? addresses[0] : undefined;
    if (defaultAddresses?.address?.id !== undefined) {
      setSelectedContactAddressId(defaultAddresses.address.id);
    } else if (firstAddress) {
      const { id } = firstAddress;
      setSelectedContactAddressId(id);
    } else {
      dispatch(addNotification(400, 'Keine Lieferadresse erstellt')); // TODO add variable translation for address type
    }
  }, [selectedContactId]);

  const saveNewAddress = useCallback(
    errors => {
      setSubmitted(true);
      if (!_isEmpty(errors)) {
        return;
      }

      setPending(true);

      const { id: contactId } = getSelectedContact();
      const url = `/api/contact/${contactId}/address.json`;
      axios
        .post(url, newAddressData)
        .then(response => {
          const { data: savedAddress } = response;
          const { id: addressId } = savedAddress;

          // Append created address to addresses
          // "select" the added address
          const contact = getSelectedContact();
          const { addresses } = contact;
          addresses.unshift(savedAddress);
          setContactAddresses(addresses);
          setSelectedContactAddressId(addressId);

          onAddressUpdate(addressType, contact, savedAddress);
        })
        .then(() => {
          // new address saved, reset the form
          setNewAddressData({ ...addressDefaults });
          setSubmitted(false);
          showSelectForm();
        })
        .catch(error => {
          console.error(error);
          dispatch(addNotification(500, error));
        })
        .finally(() => {
          setPending(false);
        });
    },
    [newAddressData]
  );

  return (
    <>
      <Collapsible icon={icon} label={label}>
        {isSelectVisible && (
          <Row>
            <Col class="m-b-30" xs={12} sm={6}>
              <label>{messages.contact}</label>
              <Select
                onChange={({ id }) => setSelectedContactId(id)}
                options={contacts}
                getOptionLabel={contactToLabel}
                getOptionValue={contactToValue}
                value={getSelectedContact() ?? {}}
              />
              <br />
              <label>
                {messages.contact} - {messages.address}
              </label>
              <Select
                onChange={({ id }) => setSelectedContactAddressId(id)}
                options={contactAddresses}
                value={getSelectedAddress()}
                getOptionValue={addressToValue}
                getOptionLabel={addressToLabel}
              />
              <IconButton
                name="fa-plus-square"
                buttonClassName="primary wizard-icon-btn m-t-5"
                label={messages.add_address}
                onClick={showNewAddressForm}
              />
            </Col>
            <Col class="m-b-30" xs={12} sm={6}>
              <CartWizardContactForm
                name="shipping"
                contact={getSelectedContact() ?? {}}
                address={getSelectedAddress() ?? {}}
              />
            </Col>
          </Row>
        )}

        {isNewAddressFormVisible && (
          <Row>
            <Col class="m-b-30" xs={12}>
              <label>New address form {messages.address}</label>
              <MetaForm metaId={addressMetaId} language="de" source={newAddressData} onChange={setNewAddressData}>
                {metaContext => {
                  const { getProperty, setProperty, errors, validate } = metaContext;

                  const onClickSave = () => validate() || setTimeout(() => saveNewAddress(errors));

                  return (
                    <>
                      <Row>
                        <Col xs={6}>
                          <FormInput
                            name="street"
                            type="text"
                            label={messages.street}
                            value={getProperty('street')}
                            validation={submitted && errors ? errors.street : undefined}
                            onChange={setProperty('street')}
                          />
                        </Col>
                        <Col xs={3}>
                          <FormInput
                            name="houseNumber"
                            type="text"
                            label={messages.house_number}
                            value={getProperty('houseNumber')}
                            validation={submitted && errors ? errors.houseNumber : undefined}
                            onChange={setProperty('houseNumber')}
                          />
                        </Col>
                        <Col xs={3}>
                          <FormInput
                            name="apartmentNumber"
                            type="text"
                            label={messages.apartment_number}
                            value={getProperty('apartmentNumber')}
                            validation={submitted && errors ? errors.apartmentNumber : undefined}
                            onChange={setProperty('apartmentNumber')}
                          />
                        </Col>
                        <Col xs={3}>
                          <FormInput
                            name="addressLine2"
                            type="text"
                            label={messages.address_line2}
                            value={getProperty('addressLine2')}
                            validation={submitted && errors ? errors.addressLine2 : undefined}
                            onChange={setProperty('addressLine2')}
                          />
                        </Col>
                      </Row>
                      <Row className="increased-input">
                        <Col xs={3}>
                          <FormInput
                            name="zipcode"
                            type="text"
                            label={messages.zip_code}
                            value={getProperty('zipcode')}
                            validation={submitted && errors ? errors.zipcode : undefined}
                            onChange={setProperty('zipcode')}
                          />
                        </Col>
                        <Col xs={5}>
                          <FormInput
                            name="city"
                            type="text"
                            label={messages.city}
                            value={getProperty('city')}
                            validation={submitted && errors ? errors.city : undefined}
                            onChange={setProperty('city')}
                          />
                        </Col>
                        <Col xs={4}>
                          <FormAutocomplete
                            name="country"
                            options={countries}
                            label={messages.country}
                            value={getProperty('country', COUNTRY_DEFAULT)}
                            validation={submitted && errors ? errors.country : undefined}
                            onChange={setProperty('country')}
                          />
                        </Col>
                      </Row>

                      <div className="text-right m-t-50">
                        <Button
                          bsStyle="default"
                          bsSize="sm"
                          type="button"
                          disabled={pending}
                          onClick={showSelectForm}
                          title={messages.cancel}
                        >
                          <i className="fal fa-times m-r-5" />
                          {messages.cancel}
                        </Button>
                        <Button bsStyle="default" bsSize="sm" type="button" disabled={pending} onClick={onClickSave}>
                          {pending ? (
                            <i className="fa-fw fa fa-circle-notch fa-spin m-r-5" />
                          ) : (
                            <i className="fa-fw fal fa-save m-r-5" />
                          )}
                          {messages.save} {messages.address}
                        </Button>
                      </div>
                    </>
                  );
                }}
              </MetaForm>
            </Col>
          </Row>
        )}
      </Collapsible>
    </>
  );
}

export default QuoteAddress;
